import React from "react";

function getNormalizedErrorMessage(error, fallbackMessage) {
  const rawMessage = String(error?.message || "").trim();

  if (!rawMessage) return fallbackMessage;
  if (rawMessage.toLowerCase().includes("network")) {
    return "Network issue detected. Please check your connection and try again.";
  }

  if (rawMessage.toLowerCase().includes("timeout")) {
    return "Request timed out. Please try again.";
  }

  return rawMessage;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.handleRetry();
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    const {
      children,
      title = "Something went wrong",
      fallbackMessage = "An unexpected error occurred in this section.",
      showReload = true,
    } = this.props;

    if (!this.state.hasError) {
      return children;
    }

    const message = getNormalizedErrorMessage(
      this.state.error,
      fallbackMessage,
    );

    return (
      <div className="min-h-[40vh] flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl w-full bg-gw2-dark-lighter border border-red-500/40 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-300 mb-2">{title}</h2>
          <p className="text-gray-300 mb-4">{message}</p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 rounded bg-gw2-accent text-white hover:bg-gw2-accent/80 transition-colors"
            >
              Try again
            </button>

            {showReload && (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded bg-gw2-dark border border-gray-600 text-gray-200 hover:border-gw2-gold hover:text-gw2-gold transition-colors"
              >
                Reload app
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
